import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, cn } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  getEpreuveWithExercises,
  epreuveExerciseTarget,
  computeEpreuveStats,
} from '@/lib/student-objectives/epreuve-lib';
import type {
  EpreuveExerciseItem,
  EpreuveExerciseStat,
  EpreuveStats,
} from '@/lib/student-objectives/epreuve-lib';
import { formatTimer } from '@/lib/exercise-v2/format-timer';

const DIFFICULTY_LABEL = { easy: 'Facile', medium: 'Moyen', hard: 'Difficile' } as const;
const DIFFICULTY_CLS = {
  easy: 'bg-xp-50 text-xp-600',
  medium: 'bg-level-50 text-level-600',
  hard: 'bg-accent-50 text-accent-600',
} as const;

function ExerciseRow({
  item,
  index,
  stat,
  isCompleted,
  isCurrent,
  onStart,
}: {
  item: EpreuveExerciseItem;
  index: number;
  /** Stat de la meilleure tentative (réussite % + temps), si l'exo est terminé. */
  stat?: EpreuveExerciseStat;
  isCompleted: boolean;
  isCurrent: boolean;
  onStart: () => void;
}) {
  // Un exercice d'épreuve est jouable dès qu'il a un id : les exos générés par
  // le pipeline PDF n'ont pas de chapitre (`chapter_id = null`) et passent par
  // la route lecteur sans chapitre.
  const canPlay = !!item.exerciseId;

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors',
        isCurrent ? 'border-primary-300 bg-primary-50' : 'border-ink-100 bg-white',
        isCompleted && 'opacity-75',
      )}
    >
      {/* Numéro / Statut */}
      <div
        className={cn(
          'font-display flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold',
          isCompleted
            ? 'bg-xp-100 text-xp-700'
            : isCurrent
              ? 'bg-primary-600 text-white'
              : 'bg-ink-100 text-ink-500',
        )}
      >
        {isCompleted ? '✓' : index + 1}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate font-sans text-[14px] font-medium',
            isCurrent ? 'text-primary-900' : 'text-ink-900',
          )}
        >
          {item.title}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span
            className={cn(
              'rounded-full px-1.5 font-sans text-[10px] font-semibold',
              DIFFICULTY_CLS[item.difficulty] ?? 'bg-ink-100 text-ink-600',
            )}
          >
            {DIFFICULTY_LABEL[item.difficulty] ?? item.difficulty}
          </span>
          {item.estimatedMinutes && (
            <span className="text-ink-400 font-sans text-[10px]">{item.estimatedMinutes} min</span>
          )}
          {!canPlay && (
            <span className="bg-ink-50 text-ink-400 rounded-full px-1.5 font-sans text-[10px]">
              Non disponible
            </span>
          )}
          {isCompleted && stat && (
            <>
              <span
                className={cn(
                  'rounded-full px-1.5 font-sans text-[10px] font-bold tabular-nums',
                  stat.score >= 60 ? 'bg-xp-50 text-xp-700' : 'bg-accent-50 text-accent-600',
                )}
              >
                {stat.score}%
              </span>
              {stat.timeMs > 0 && (
                <span className="text-ink-400 font-sans text-[10px] tabular-nums">
                  ⏱ {formatTimer(stat.timeMs)}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Action */}
      {canPlay && (
        <Button
          size="sm"
          variant={isCompleted ? 'outline' : isCurrent ? 'primary' : 'outline'}
          onClick={onStart}
          className="min-w-[80px] shrink-0"
        >
          {isCompleted ? 'Refaire' : isCurrent ? 'Commencer' : 'Démarrer'}
        </Button>
      )}
    </div>
  );
}

export function EpreuvePage() {
  const { epreuveId } = useParams({ strict: false }) as { epreuveId: string };
  const { user } = useAuth();
  const navigate = useNavigate();
  const studentId = user?.id ?? '';

  const [started, setStarted] = useState(false);

  const { data: epreuve, isLoading } = useQuery({
    queryKey: ['epreuve', epreuveId],
    enabled: !!epreuveId,
    queryFn: () => getEpreuveWithExercises(supabase, epreuveId),
    staleTime: 60_000,
  });

  // Tentatives complétées de l'élève sur ces exercices → réussite % + temps,
  // par exercice et en global (cf. computeEpreuveStats).
  const exerciseIds = epreuve?.exercises.map((e) => e.exerciseId) ?? [];
  const EMPTY_STATS: EpreuveStats = {
    byExercise: {},
    completedCount: 0,
    avgScore: 0,
    totalTimeMs: 0,
  };
  const { data: stats = EMPTY_STATS } = useQuery({
    queryKey: ['epreuve-attempts', epreuveId, studentId],
    enabled: !!studentId && exerciseIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('attempts')
        .select('exercise_id, score, started_at, completed_at')
        .eq('student_id', studentId)
        .eq('status', 'completed')
        .in('exercise_id', exerciseIds);
      const rows = (data ?? [])
        .filter((r): r is typeof r & { exercise_id: string } => r.exercise_id != null)
        .map((r) => ({
          exercise_id: r.exercise_id,
          score: r.score,
          started_at: r.started_at,
          completed_at: r.completed_at,
        }));
      return computeEpreuveStats(rows);
    },
    staleTime: 30_000,
  });
  const completedIds = new Set(Object.keys(stats.byExercise));

  function handleStartExercise(item: EpreuveExerciseItem) {
    const target = epreuveExerciseTarget(item);
    if (!target) return;
    navigate(target);
  }

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!epreuve) {
    return (
      <div className="pb-safe mx-auto max-w-2xl px-4 py-8 text-center">
        <p className="text-ink-500">Épreuve introuvable.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/student/dashboard' })}
        >
          ← Retour
        </Button>
      </div>
    );
  }

  const exercises = epreuve.exercises;
  const completedCount = exercises.filter((e) => completedIds.has(e.exerciseId)).length;
  const allDone = completedCount === exercises.length && exercises.length > 0;

  // Premier exercice non complété (pour le bouton "Reprendre")
  const nextExercise = exercises.find((e) => !completedIds.has(e.exerciseId));

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      {/* Retour */}
      <Button
        variant="ghost"
        size="sm"
        className="text-ink-500 -ml-2 mb-6"
        onClick={() => navigate({ to: '/student/dashboard' })}
      >
        ← Retour
      </Button>

      {!started ? (
        /* ── Écran d'accueil ─────────────────────────────── */
        <div className="space-y-6">
          <div>
            <p className="font-display text-primary-600 mb-1 text-[11px] font-semibold uppercase tracking-widest">
              {epreuve.examType
                ? `${epreuve.examType.charAt(0).toUpperCase()}${epreuve.examType.slice(1)}${epreuve.examYear ? ` ${epreuve.examYear}` : ''}`
                : 'Épreuve'}
            </p>
            <h1 className="font-display text-ink-900 text-2xl font-extrabold">{epreuve.title}</h1>
            {epreuve.description && (
              <p className="text-ink-500 mt-2 font-sans text-[14px]">{epreuve.description}</p>
            )}
          </div>

          {/* Résumé */}
          <div className="bg-ink-50 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl px-5 py-4">
            <div className="text-center">
              <p className="font-display text-ink-900 text-[22px] font-extrabold">
                {exercises.length}
              </p>
              <p className="text-ink-500 font-sans text-[11px]">
                exercice{exercises.length !== 1 ? 's' : ''}
              </p>
            </div>
            {completedCount > 0 && (
              <>
                <div className="text-center">
                  <p className="font-display text-xp-600 text-[22px] font-extrabold">
                    {completedCount}
                  </p>
                  <p className="text-ink-500 font-sans text-[11px]">
                    terminé{completedCount > 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-display text-primary-600 text-[22px] font-extrabold tabular-nums">
                    {stats.avgScore}%
                  </p>
                  <p className="text-ink-500 font-sans text-[11px]">réussite</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-ink-900 text-[22px] font-extrabold tabular-nums">
                    {formatTimer(stats.totalTimeMs)}
                  </p>
                  <p className="text-ink-500 font-sans text-[11px]">temps total</p>
                </div>
              </>
            )}
          </div>

          {/* Liste des exercices */}
          <div className="space-y-2">
            {exercises.map((item, idx) => (
              <ExerciseRow
                key={item.exerciseId}
                item={item}
                index={idx}
                stat={stats.byExercise[item.exerciseId]}
                isCompleted={completedIds.has(item.exerciseId)}
                isCurrent={false}
                onStart={() => handleStartExercise(item)}
              />
            ))}
          </div>

          {/* CTA principal */}
          {!allDone && (
            <Button
              className="w-full"
              onClick={() => {
                if (nextExercise) {
                  handleStartExercise(nextExercise);
                } else {
                  setStarted(true);
                }
              }}
            >
              {completedCount > 0 ? 'Continuer l’épreuve' : 'Commencer l’épreuve'}
            </Button>
          )}

          {allDone && (
            <div className="bg-xp-50 rounded-2xl px-5 py-4 text-center">
              <p className="font-display text-xp-700 text-[17px] font-bold">Épreuve terminée !</p>
              <p className="text-xp-600 mt-1 font-sans text-[13px]">
                Vous avez complété tous les exercices.
              </p>
            </div>
          )}
        </div>
      ) : (
        /* ── Vue liste pour choisir un exercice ──────────── */
        <div className="space-y-4">
          <h1 className="font-display text-ink-900 text-xl font-extrabold">{epreuve.title}</h1>
          <div className="space-y-2">
            {exercises.map((item, idx) => (
              <ExerciseRow
                key={item.exerciseId}
                item={item}
                index={idx}
                stat={stats.byExercise[item.exerciseId]}
                isCompleted={completedIds.has(item.exerciseId)}
                isCurrent={item === nextExercise}
                onStart={() => handleStartExercise(item)}
              />
            ))}
          </div>
          <Button variant="outline" className="w-full" onClick={() => setStarted(false)}>
            ← Retour à l'accueil
          </Button>
        </div>
      )}
    </div>
  );
}
